import queryString from 'qs'
import history from './history'

import { toSingleValueInteger, toSingleValue } from 'utils/converters'

function parseFacetsFromQueryParams(queryParams) {
    return Object.keys(queryParams)
        .filter(x => x.startsWith('f_'))
        .map(key => ({
            field: key,
            values: decodeURIComponent(queryParams[key]).split('|'),
        }))
}

function parsePageNumberFromQueryParams(queryParams) {
    return toSingleValueInteger(queryParams.pagenumber) || 1
}

function parseSearchTermFromQueryParams(queryParams) {
    return toSingleValue(queryParams.q)
}

function parseOrderByFromQueryParams(queryParams) {
    return toSingleValue(queryParams.orderby)
}

function parseCategoryFromPathName() {
    const pathName = window.location.pathname
    return toSingleValue(pathName.substr(pathName.indexOf('/') + 1))
}

function getPriceRangeFromString(range) {
    let priceRange;

    try {
        priceRange = [range.split('-')[0], range.split('-')[1]]
    }
    catch (ex) {
        console.warn('Couldnt parse price range')
    }
    
    return priceRange
}

function getPriceRangeFromArray(range) {
    if (Array.isArray(range)) {
        const [min, max] = range

        if (min == undefined && max == undefined) {
            return undefined
        }

        return `${min}-${max}`
    }
    
    console.warn('PriceRange not an array')
}

function parsePriceRange(range) {
    if (Array.isArray(range)) {
        return getPriceRangeFromArray(range)
    }

    if (range !== 'object' && typeof (range) == 'string') {
        return getPriceRangeFromString(range)
    }

    if (range && range.priceRange) parsePriceRange(range.priceRange)
}

function isSearchStringUpdated(nextSearchString, history) {
    const [nextPageSize, nextPriceRange, nextProductType] = nextSearchString.split('&')

    const [prevPageSize, prevPriceRange, prevProductType] = history
        .replace('?', '')
        .split('&')

    if (
        prevPageSize === nextPageSize &&
        prevPriceRange === nextPriceRange &&
        prevProductType === nextProductType
    ) {
        return false
    }

    return true
}

function isSearchPathUpdated(nextPath, history) {
    const prevPath = history.replace('/', '')

    if (nextPath === prevPath) {
        return false
    }

    return true
}

function paramsToState(queryParams) {
    const state = {
        pageNumber: parsePageNumberFromQueryParams(queryParams),
        searchTerm: parseSearchTermFromQueryParams(queryParams),
        category: parseCategoryFromPathName(),
        facets: parseFacetsFromQueryParams(queryParams),
        orderBy: parseOrderByFromQueryParams(queryParams),
        priceRange: parsePriceRange(queryParams.priceRange),
        pageSize: Number(queryParams.pagesize),
    }

    return state
}

function stateToParams({
    searchTerm,
    pageNumber,
    pageSize,
    orderBy,
    facets,
    priceRange,
}) {
    const params = {}

    if (pageNumber > 1) params.pagenumber = pageNumber
    if (pageSize > 1) params.pagesize = pageSize
    if (searchTerm) params.q = searchTerm
    if (orderBy) params.orderby = orderBy
    if (priceRange) params.priceRange = parsePriceRange(priceRange)

    const facetKeys = Object.keys(facets)

    if (facetKeys && facetKeys.length > 0) {
        facetKeys.forEach(key => {
            params[facets[key].field] = facets[key].values.join('|')
        })
    }

    return params
}

function stateToQueryString(state) {
    return queryString.stringify(stateToParams(state))
}

function stateToPathName(state) {
    return state.category
}

class UrlManager {
    constructor() {
        this.history = history
        this.lastPushSearchString = ''
        this.lastPushSearchPath = ''

        this.isSearchPathUpdated = isSearchPathUpdated
        this.isSearchStringUpdated = isSearchStringUpdated

        const params = paramsToState(
            queryString.parse(this.history.location.search, {
                ignoreQueryPrefix: true,
            }),
        )

        this.state = params
    }

    getStateFromUrl() {
        return paramsToState(
            queryString.parse(this.history.location.search, {
                ignoreQueryPrefix: true,
            }),
        )
    }

    pushStateToUrl(state) {
        const searchString = stateToQueryString(state)
        const searchPath = stateToPathName(state)

        this.lastPushSearchPath = searchPath
        this.lastPushSearchString = searchString

        this.history.push({
            pathname: `/${searchPath}`,
            search: `?${searchString}`,
        })
    }

    getRequestUrl(state) {
        const searchString = stateToQueryString(state)
        const searchPath = stateToPathName(state)

        this.lastPushSearchPath = searchPath
        this.lastPushSearchString = searchString

        return `${`${window.location.origin}/${searchPath}`}?${searchString}`
    }

    /**
     * Add an event handler to be executed whenever state is pushed to the URL
     *
     * @callback requestCallback
     * @param {Object} state - Updated application state parsed from the new URL
     *
     * @param {requestCallback} callback
     */
    onURLStateChange(callback) {
        this.unlisten = this.history.listen(history => {
            // If this URL is updated as a result of a pushState request, we don't
            // want to notify that the URL changed.
            
            // Check what values changed
            const isSearchStringUpdated = this.isSearchStringUpdated(
                this.lastPushSearchString,
                history.location.search,
            )
                
            const isSearchPathUpdated = this.isSearchPathUpdated(
                this.lastPushSearchPath,
                history.location.pathname,
            )

            if (!isSearchStringUpdated && !isSearchPathUpdated) return

            // Once we've decided to return based on lastPushSearchString, reset
            // it so that we don't break back / forward button.
            this.lastPushSearchString = ''
            this.lastPushSearchPath = ''

            callback(
                paramsToState(
                    queryString.parse(history.location.search, {
                        ignoreQueryPrefix: true,
                    }),
                ),
            )
        })
    }
}

export default UrlManager